import { map, mergeMap, scan, shareReplay, tap } from 'rxjs/operators';
import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable, ReplaySubject } from 'rxjs';

import { AddonActivation } from './accounts-api/activation';
import { Account, AccountNapData } from './accounts-api/account.model';
import { AccountsApiService } from './accounts-api/accounts.api.service';
import { AccountNapDataApiInterface } from './accounts-api/account.api.interface';

interface AccountResponse {
  accounts: Account[];
  appId: string;
}

@Injectable()
export class AccountService {
  private appId: string;
  private partnerId: string;
  private accountsMap: Observable<{ [appId: string]: Account[] }>;
  private responses: Observable<AccountResponse>;
  private requests: ReplaySubject<{ appId: string; partnerId: string; isLoadMore: boolean }> = new ReplaySubject(1);

  private loading$$: BehaviorSubject<boolean> = new BehaviorSubject(false);

  constructor(private accountsApiService: AccountsApiService) {
    this.responses = this.requests.pipe(
      mergeMap((request) => {
        this.loading$$.next(true);
        if (request.isLoadMore && this.hasMore) {
          return this.accountsApiService.fetchNext(request.appId).pipe(
            map((accounts) => {
              return { accounts: accounts, appId: request.appId } as AccountResponse;
            }),
            tap(() => this.loading$$.next(false)),
          );
        } else {
          return this.accountsApiService.fetch(request.appId, request.partnerId).pipe(
            map((accounts) => {
              return { accounts: accounts, appId: request.appId } as AccountResponse;
            }),
            tap(() => this.loading$$.next(false)),
          );
        }
      }),
    );

    this.accountsMap = this.responses.pipe(
      scan((currentMap, accountsResponse: AccountResponse) => {
        if (!accountsResponse || accountsResponse.accounts.length === 0) {
          return currentMap;
        }

        const appId = accountsResponse.appId;
        if (!currentMap[appId] || currentMap[appId].length === 0) {
          currentMap[appId] = accountsResponse.accounts;
        }

        // Replace any accounts that may already be in the list (we
        // refreshed them) exist.
        const accountMap = {};
        accountsResponse.accounts.forEach((account) => {
          accountMap[account.id] = account;
        });

        currentMap[appId] = currentMap[appId].map((account) => {
          if (accountMap[account.id]) {
            const replacementAccount = accountMap[account.id];
            delete accountMap[account.id];
            return replacementAccount;
          }
          return account;
        });

        // Add in additional accounts
        const additionalAccounts = Object.values<Account>(accountMap);
        currentMap[appId] = currentMap[appId].concat(additionalAccounts);

        return currentMap;
      }, {}),
      shareReplay(1),
    );
  }

  public get loading(): boolean {
    return this.loading$$.getValue();
  }

  public get loading$(): Observable<boolean> {
    return this.loading$$.asObservable();
  }

  public get hasMore(): boolean {
    return this.accountsApiService.hasMore;
  }

  load(appId: string, partnerId?: string): Observable<Account[]> {
    const accounts = this.accountsMap.pipe(
      map((accountsMap) => {
        if (this.partnerId) {
          return accountsMap[this.appId]
            ? accountsMap[this.appId].filter((account) => account.partnerId === this.partnerId)
            : [];
        } else {
          return accountsMap[this.appId] || [];
        }
      }),
    );

    if (appId !== this.appId || partnerId !== this.partnerId) {
      this.appId = appId;
      this.partnerId = partnerId;
      this.requests.next({ appId: appId, partnerId: partnerId, isLoadMore: false });
    }

    return accounts;
  }

  loadMore(): void {
    this.requests.next({ appId: this.appId, partnerId: this.partnerId, isLoadMore: true });
  }

  getAccount(appId: string, accountId: string): Observable<Account> {
    return this.accountsApiService.fetchAccount(appId, accountId).pipe(shareReplay(1));
  }

  listActiveAddons(appId: string, accountId: string): Observable<AddonActivation[]> {
    const addonActivations = this.accountsApiService.listAddonActivations(appId, accountId);
    return addonActivations.pipe(
      map((activations) => activations.filter((activation) => !activation.deactivated)),
      shareReplay(1),
    );
  }

  getMulti(accountGroupIds: string[]): Observable<AccountNapData[]> {
    return this.accountsApiService
      .accountGroupNapDataGetMulti(accountGroupIds)
      .pipe(
        map((resp: AccountNapDataApiInterface[]) =>
          resp.map((napData: AccountNapDataApiInterface) => AccountNapData.fromApiModel(napData)),
        ),
      );
  }
}
